import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AccountResponse } from '../../models/account/AccountResponse';
import { ProjectResponse } from '../../models/projects/ProjectResponse';
import useDataLayer, { IUserDataDataLayer } from '../../hooks/global/UseDataLayer';
import { getAccount } from '../../services/account/AccountService';
import { getProjects } from '../../services/projects/ProjectService';

interface AccountContextType {
  accountResponse: AccountResponse | null;
  setAccountResponse: Dispatch<SetStateAction<AccountResponse | null>>;
  projectResponse: ProjectResponse[] | null;
  setProjectResponse: Dispatch<SetStateAction<ProjectResponse[] | null>>;
  showOnboarding: boolean;
  setShowOnboarding: Dispatch<SetStateAction<boolean>>;
  showVideoTutorial: boolean;
  setShowVideoTutorial: Dispatch<SetStateAction<boolean>>;
  refetchData: () => void;
  refetchAccountSilently: () => Promise<void>;
  refetchProjectsSilently: () => Promise<void>;
}

export const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [showVideoTutorial, setShowVideoTutorial] = useState<boolean>(false);
  const [accountResponse, setAccountResponse] = useState<AccountResponse | null>(null);
  const [projectResponse, setProjectResponse] = useState<ProjectResponse[] | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  const { pushDataLayer } = useDataLayer();
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  const refetchData = useCallback(() => {
    setAccountResponse(null);
    setProjectResponse(null);
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  const refetchAccountSilently = useCallback(async () => {
    try {
      const freshAccount = await getAccount();
      if (freshAccount) {
        setAccountResponse(freshAccount);
      }
    } catch (error) {
      console.error('Silent account refetch failed:', error);
    }
  }, []);

  const refetchProjectsSilently = useCallback(async () => {
    try {
      const freshProjects = await getProjects();
      if (freshProjects) {
        setProjectResponse(freshProjects);
      }
    } catch (error) {
      console.error('Silent projects refetch failed:', error);
    }
  }, []);

  // Send data layer event when location changes and user is authenticated
  useEffect(() => {
    if (accountResponse && location.pathname !== lastPath.current) {
      pushDataLayer<IUserDataDataLayer>({
        event: 'user_data',
        email_hashed: accountResponse.email,
        user_id: accountResponse.userId?.toString(),
        account_type: accountResponse.accountType,
        account_id: accountResponse.accountId.toString(),
      });
      lastPath.current = location.pathname;
    }
  }, [location.pathname, accountResponse, pushDataLayer]);

  return (
    <AccountContext.Provider
      value={{
        showOnboarding,
        setShowOnboarding,
        showVideoTutorial,
        setShowVideoTutorial,
        accountResponse,
        setAccountResponse,
        projectResponse,
        setProjectResponse,
        refetchData,
        refetchAccountSilently,
        refetchProjectsSilently,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw Error('useAccount must be used with an AccountProvider');
  }

  return context;
};
